import React, { useState, useMemo, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import moment from "moment";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import FilterApplicationRequests from "Components/Common/FilterApplicationRequest";
import ApplicationViewModal from "Components/ApplicationViewModal";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import ApplicationEditModal from "Components/ApplicationEditModal";
import ApplicationStatusHistoryChart from "Components/Charts/ApplicationStatusHistoryChart";
import PaymentRequest from "Components/PaymentRequest";
import Modal from "Components/Common/Modal";
import DownloadUploadActionButton from "./DownloadUploadActionButton";
import Tooltip from "Components/Common/Tooltip";
import PartialDocumentUploadModal from "./PartialDocumentUploadModal";
import BankInformationFormModal from "pages/Consumer/BankInformationFormModal";
import DemandEstimatePaymentModal from "./DemandEstimatePaymentModal";
import { ChartIcon } from "Components/Common/Styled/ChartIcon";
import {
  applicationStatusTypes,
  applicationTypes,
  estimateIssued,
} from "common/constant";

import { getImageUrl } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { formatUtilityAddress } from "utils/formatUtilityAddress";

import { usePostRefundCalculationPaymentMutation } from "apis/DemandEstimation";

import { APPLICATION_COMMENT, APPLICATION_DETAILS } from "tagTypes";

const RECORDS_PER_PAGE = 10;

const editableStatus = ["saved", "commented", "submitted"];
const pendingDocumentNonUploadableStatus = ["saved", "submitted", "rejected"];

const ApplicationRequestList = ({ t }) => {
  const [query, setQuery] = useState({});

  const lg = useMediaQuery("(min-width: 768px) and (max-width: 1399px)");
  const md = useMediaQuery("(min-width: 576px) and (max-width: 767px)");
  const sm = useMediaQuery("(max-width: 575px)");

  const [editApplicationId, setEditApplicationId] = useState("");
  const [applicationTypeId, setApplicationTypeId] = useState("");
  const [applicationTypeName, setApplicationTypeName] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [finalRefundAmountToConsumer, setFinalRefundAmountToConsumer] =
    useState("");
  const [
    isPreviewRefundCalculationBreakupModalOpen,
    setIsPreviewRefundCalculationBreakupModalOpen,
  ] = useState(false);
  const [applicationIdForRefundPayment, setApplicationIdForRefundPayment] =
    useState("");
  const [bankInformationModalProps, setBankInformationModalProps] =
    useState(null);

  const [isDemandEstimateModalOpen, setIsDemandEstimateModalOpen] =
    useState(false);
  const [isStatusHistoryChartModalOpen, setIsStatusHistoryChartModalOpen] =
    useState(false);
  const [
    isDemandEstimatePaymentModalOpen,
    setIsDemandEstimatePaymentModalOpen,
  ] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState();

  const [getPaymentUrlForRefundCalculation] =
    usePostRefundCalculationPaymentMutation();

  const resetPageRef = useRef();

  const [searchParams, setSearchParams] = useSearchParams();
  const viewApplicationId = searchParams.get("applicationId");
  const applicationRequestId = searchParams.get("applicationRequestId");

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const columns = [
    {
      title: "Application Number",
      field: "applicationNumber",
    },
    {
      title: "Application Type",
      field: "applicationType.name",
    },
    {
      title: "Name",
      field: "name",
    },
    {
      title: "Utility Address",
      renderCell: ({ utilityAddress }) => {
        const address = formatUtilityAddress(utilityAddress);

        if (address) {
          return (
            <>
              <p className="m-0">{address}, Gandhinagar-382050</p>
            </>
          );
        }

        return "--";
      },
      columnProps: { style: { width: "350px", minWidth: "350px" } },
    },
    {
      title: "Date",
      renderCell: ({ submissionDate }) =>
        moment(submissionDate).isValid()
          ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
          : "N/A",
    },
    {
      title: "Status",
      renderCell: (item) => {
        return (
          <>
            {item.applicationStatus.name}
            {item?.applicationStatus?.name?.toLowerCase() === "approved" &&
            !item?.approvedApplicationDocumentURL ? (
              <Tooltip
                title={t(
                  "Your application request has been approved. Please download the application form, sign it, and then upload it again"
                )}
              >
                <InfoOutlinedIcon
                  className="ms-1 cursor-pointer"
                  color="primary"
                  fontSize="small"
                />
              </Tooltip>
            ) : item?.applicationStatus?.name?.toLowerCase() === "rejected" &&
              !item?.isTechnicalFeasibility ? (
              <Tooltip
                title={t(
                  "Location is not technically feasible for this application."
                )}
              >
                <InfoOutlinedIcon
                  className="ms-1 cursor-pointer"
                  color="primary"
                  fontSize="small"
                />
              </Tooltip>
            ) : (
              ""
            )}
          </>
        );
      },
    },
    {
      title: "Actions",
      // columnProps:{style={textAlign:"center"}}
      renderCell: (item) => {
        const {
          applicationStatus,
          applicationType: { id: applicationTypeId, name: applicationTypeName },
          id,
          demandEstimateId,
          demandEstimateUrl,
          isPartialDocumentsUploaded,
          isAllPendingDocumentsApproved,
          refundCalculationUrl,
          isRefundCalculatedByFinance,
          finalRefundAmount,
          utilityConsumerId,
        } = item;
        const isEditDisable = !editableStatus.includes(
          applicationStatus.name.toLowerCase()
        );

        const applicationStatusName = applicationStatus.name.toLowerCase();

        const isUploadDocumentsIconVisible =
          !pendingDocumentNonUploadableStatus.includes(applicationStatusName) &&
          (!isPartialDocumentsUploaded || !isAllPendingDocumentsApproved);

        const isEstimationIssued = applicationStatusName === estimateIssued;

        const isMeterRemovedStatus =
          applicationStatus.id === applicationStatusTypes["Disconnected"];

        return (
          <Box width="150px">
            <IconButton
              onClick={() => {
                setApplicationTypeId(applicationTypeId);
                setApplicationTypeName(applicationTypeName);
                searchParams.set("applicationId", id);
                setSearchParams(searchParams);
              }}
            >
              <Tooltip title={t("View Application Form")}>
                <RemoveRedEyeIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>

            {/* Edit */}
            {!isEstimationIssued && (
              <IconButton
                disabled={isEditDisable}
                onClick={() => {
                  setApplicationTypeId(applicationTypeId);
                  setEditApplicationId(id);
                }}
              >
                <Tooltip title={t("Edit Application Form")}>
                  <EditIcon
                    fontSize="small"
                    className="cursor-pointer"
                    color={isEditDisable ? "disabled" : "primary"}
                  />
                </Tooltip>
              </IconButton>
            )}

            {/* Download and upload form */}
            <DownloadUploadActionButton item={item} />

            {/* Pay now */}
            {isEstimationIssued && (
              <IconButton
                onClick={() => {
                  setIsDemandEstimateModalOpen(true);
                  setApplicationId(id);
                  setDocumentToPreview({
                    demandEstimateId: demandEstimateId,
                    url: getImageUrl(demandEstimateUrl),
                    documentTypeName: "Demand Estimate",
                    documentType: "pdf",
                    documentName: "demandEstimate",
                  });
                }}
              >
                <Tooltip title={t("Pay Now")}>
                  <CreditCardIcon
                    fontSize="medium"
                    className="cursor-pointer"
                    color="primary"
                    sx={{ width: "23px" }}
                  />
                </Tooltip>
              </IconButton>
            )}

            {/* Upload Documents */}
            {isUploadDocumentsIconVisible && (
              <IconButton
                onClick={() => {
                  searchParams.set("applicationRequestId", id);
                  setSearchParams(searchParams);
                }}
              >
                <Tooltip title={t("Upload Documents")}>
                  <UploadFileIcon
                    fontSize="small"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}

            {isRefundCalculatedByFinance &&
              finalRefundAmount < 0 &&
              isMeterRemovedStatus && (
                <Tooltip title="Pay Now">
                  <IconButton
                    onClick={() => {
                      setApplicationIdForRefundPayment(id);
                      setFinalRefundAmountToConsumer(finalRefundAmount);
                    }}
                    size="small"
                  >
                    <CreditCardIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}

            {!!refundCalculationUrl && (
              <Tooltip title="Refund Calculations Document">
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(refundCalculationUrl),
                      documentTypeName: "Refund Calculations Breakup Document",
                      documentType: "pdf",
                      documentName: "Refund Calculations Breakup",
                    });
                    setIsPreviewRefundCalculationBreakupModalOpen(true);
                  }}
                  size="small"
                >
                  <InsertDriveFileIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}

            {!!isMeterRemovedStatus &&
              applicationTypeId === applicationTypes["Removal of Connection"] &&
              !!finalRefundAmount &&
              finalRefundAmount > 0 &&
              isRefundCalculatedByFinance && (
                <Tooltip title={t("Bank Information")}>
                  <IconButton
                    onClick={() => {
                      setBankInformationModalProps({
                        utilityConsumerId,
                        applicationId: id,
                      });
                    }}
                    size="small"
                  >
                    <AccountBalanceIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}

            <IconButton
              onClick={() => {
                setApplicationId(id);
                setIsStatusHistoryChartModalOpen(true);
              }}
            >
              <Tooltip title={t("Application Status History Chart")}>
                <ChartIcon />
              </Tooltip>
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/power/application",
      tags: [APPLICATION_DETAILS, APPLICATION_COMMENT],
      query,
    }),
    [query]
  );

  return (
    <>
      {isDemandEstimatePaymentModalOpen && !!applicationId && (
        <DemandEstimatePaymentModal
          applicationId={applicationId}
          handleClose={() => setIsDemandEstimatePaymentModalOpen(false)}
          handleCloseForSubmit={() => {
            setApplicationId("");
            setIsDemandEstimateModalOpen(false);
          }}
          isPowerUtility
        />
      )}

      {!!applicationIdForRefundPayment && (
        <Modal
          isOpen
          handleClose={() => {
            setFinalRefundAmountToConsumer("");
            setApplicationIdForRefundPayment("");
          }}
          title="Refund Calculations Payment"
          maxWidth="sm"
        >
          <div className="mt-3">
            <Typography>
              You need to pay
              <b> RS. {Math.abs(finalRefundAmountToConsumer)}/- </b>
              to complete the application process.
            </Typography>
          </div>

          <div className="text-end mt-3">
            <PaymentRequest
              generateEncryption={async () =>
                await getPaymentUrlForRefundCalculation({
                  applicationId: applicationIdForRefundPayment,
                })
              }
              isPowerUtility
            >
              <Button variant="contained">{t("Pay Now")}</Button>
            </PaymentRequest>
          </div>
        </Modal>
      )}

      {isPreviewRefundCalculationBreakupModalOpen && !!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setIsPreviewRefundCalculationBreakupModalOpen(false);
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}
      {!!(applicationTypeId && editApplicationId) && (
        <ApplicationEditModal
          applicationTypeId={applicationTypeId}
          applicationId={editApplicationId}
          handleClose={() => setEditApplicationId("")}
        />
      )}

      {!!documentToPreview && isDemandEstimateModalOpen && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
            setApplicationId("");
            setIsDemandEstimateModalOpen(false);
          }}
          actionButton={
            <Button
              variant="contained"
              onClick={() => setIsDemandEstimatePaymentModalOpen(true)}
            >
              {t("Pay Now")}
            </Button>
          }
          {...documentToPreview}
        />
      )}

      {!!bankInformationModalProps && (
        <BankInformationFormModal
          bankInformationModalProps={bankInformationModalProps}
          handleClose={() => {
            setBankInformationModalProps(null);
          }}
        />
      )}

      {isStatusHistoryChartModalOpen && !!applicationId && (
        <Modal
          isOpen
          handleClose={() => {
            setApplicationId("");
            setIsStatusHistoryChartModalOpen(false);
          }}
          title="Application Status History Chart"
          maxWidth="md"
        >
          <ApplicationStatusHistoryChart
            applicationId={applicationId}
            handleClose={() => setApplicationId("")}
          />
        </Modal>
      )}

      {!!viewApplicationId && (
        <ApplicationViewModal
          applicationId={viewApplicationId}
          applicationType={applicationTypeName}
          handleClose={() => {
            searchParams.delete("applicationId");
            setSearchParams(searchParams);
          }}
        />
      )}

      {!!applicationRequestId && (
        <PartialDocumentUploadModal isPowerApplication />
      )}

      <div>
        <BreadCrumb title={t("Application Request Details")} />
        <FilterApplicationRequests
          setQuery={setQuery}
          resetPage={handleResetPage}
        />

        <BaseTable
          columns={columns}
          pageSize={RECORDS_PER_PAGE}
          paginationType="serverSide"
          serverSidePaginationConfig={serverSidePaginationConfig}
          registerResetPage={(cb) => {
            resetPageRef.current = cb;
            setIsResetPageRegistered((prev) => !prev);
          }}
          maxHeight={
            sm
              ? "calc(100vh - 400px)"
              : md
              ? "calc(100vh - 360px)"
              : lg
              ? "calc(100vh - 320px)"
              : "calc(100vh - 270px)"
          }
        />
      </div>
    </>
  );
};

ApplicationRequestList.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(ApplicationRequestList);
